import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FieldProps } from 'src/modules/common/components/DecimalField/types';
import { DecimalInput } from 'src/modules/common/components/DecimalField/DecimalInput';
import { ConfigContext } from 'src/configContext.ts';
import { formatNumericString } from 'src/utils/formatNumericString.ts';
import { numericToLocalized } from 'src/utils/numericToLocalized';
import { DECIMAL_DIGITS } from '../../constants';

export const DecimalField: (props: FieldProps) => React.ReactElement = ({
    name,
    value,
    onValueChange,
    labelText,
    currentLocale,
    maxValue,
    ...restProps
}) => {
    const {
        numberFormat: { thousandSeparator, decimalSeparator },
    } = useContext(ConfigContext);
    const [formattedValue, setFormattedValue] = useState('');

    const localizedToNumeric = useCallback((localized: string) => localized
        .replace(new RegExp(`\\${thousandSeparator}`, 'g'), '')
        .replace(new RegExp(`\\${decimalSeparator}`, 'g'), '.'), [decimalSeparator, thousandSeparator]);

    const onChangeHandle: React.ChangeEventHandler<HTMLInputElement> = useCallback((e) => {
        const caret = Number(e.target.selectionStart);
        const element = e.target;

        const maximum = maxValue || Number.MAX_SAFE_INTEGER;

        const inputValue = e.target.value;
        const numeric = localizedToNumeric(inputValue);
        const formattedString = formatNumericString(numeric);

        // validation failed
        if (formattedString === null) {
            // not allow to move the caret when the input is invalid
            window.requestAnimationFrame(() => {
                element.selectionStart = caret - 1;
                element.selectionEnd = caret - 1;
            });
            return;
        }

        const lastCharacter = inputValue.slice(-1);

        // cover the case of incomplete decimal, when the user only put the dot
        if (lastCharacter === decimalSeparator) {
            const head = numeric.slice(0, -1);
            const incompleteFormatted = `${numericToLocalized(head, DECIMAL_DIGITS, currentLocale)}${decimalSeparator}`;
            setFormattedValue(incompleteFormatted);
            return;
        }

        const formattedStringLimited = Number(formattedString) > maximum ? String(maximum) : formattedString;

        if (formattedStringLimited !== value) {
            onValueChange(formattedStringLimited);
        }

        const localized = numericToLocalized(formattedStringLimited, DECIMAL_DIGITS, currentLocale);
        const lengthDiff = localized.length - inputValue.length;

        // move the caret considering new symbols after localization formatting
        window.requestAnimationFrame(() => {
            element.selectionStart = caret + lengthDiff;
            element.selectionEnd = caret + lengthDiff;
        });
        setFormattedValue(numericToLocalized(formattedStringLimited, DECIMAL_DIGITS, currentLocale));
    }, [onValueChange, value, localizedToNumeric, currentLocale, decimalSeparator, maxValue]);

    useEffect(() => {
        const formattedString = formatNumericString(value);

        if (formattedString !== null) {
            const localized = numericToLocalized(formattedString, DECIMAL_DIGITS, currentLocale);
            setFormattedValue(localized);
        }
    }, [value, currentLocale]);

    return (
        <DecimalInput
            {...restProps}
            type="text"
            id={name}
            name={name}
            value={formattedValue}
            onChange={onChangeHandle}
            labelText={labelText}
        />
    );
};
